import React, { useState } from 'react';
import { api } from 'api';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { Account } from 'common/state/accounts/actions';
import { TableTitle } from 'common/components/tables';
import { transferIcon } from 'common/icons';
import { AccountTransfer } from 'common/state/accounts/transfers/actions';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { AccountTransfersActions } from 'common/state/accounts/transfers/actions';
import { accountTransfersRecipe } from 'common/state/accounts/transfers/recipes';
import {
  AccountTransfersState,
  defaultAccountTransfersState,
} from 'common/state/accounts/transfers/states';
import { doGetAccountTransfers } from 'common/state/accounts/transfers/thunkActions';

export const TransfersTable = () => {
  const [state, dispatch] = useProducerWithThunks<
    AccountTransfersState,
    AccountTransfersActions
  >(accountTransfersRecipe, defaultAccountTransfersState);
  const [accounts, setAccounts] = useState<{ [url: string]: Account }>({});
  const [requesters, setRequesters] = useState<{ [url: string]: Account }>({});
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const columns = useMemo(
    (): ColumnProp<AccountTransfer>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.date).toLocaleString(),
      },
      { key: 'id', title: 'Reference' },
      {
        key: 'description',
        title: 'Description',
        valueFormatter: (row) =>
          `${accounts?.[row.account]?.system_name || 'Loading'}: ` +
          `${accounts?.[row.account]?.username || 'Loading'}, requested by ` +
          `${requesters?.[row.requester]?.username || 'Loading'}`,
        width: 360,
      },
      {
        key: 'currency',
        title: 'Currency',
      },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
      },
    ],
    [accounts, requesters]
  );

  useEffect(() => {
    dispatch(
      doGetAccountTransfers({
        limit: pageSize,
        offset: pageSize * (pageNo - 1),
      })
    );
  }, [dispatch, pageSize, pageNo]);

  useEffect(() => {
    if (!state.transfers) return;
    const [accountUrls, requesterUrls] = state.transfers.reduce(
      (acc, transfer) => {
        if (!(transfer.account in accounts)) acc[0].add(transfer.account);
        if (!(transfer.requester in requesters)) acc[1].add(transfer.requester);
        return acc;
      },
      [new Set<string>(), new Set<string>()]
    );

    if (accountUrls.size) {
      Promise.all(Array.from(accountUrls).map((url) => api.get(url))).then(
        (accountResponses) =>
          setAccounts(
            accountResponses.reduce(
              (acc, accountResponse) => ({
                ...acc,
                [accountResponse.data.url]: accountResponse.data,
              }),
              accounts
            )
          )
      );
    }
    if (requesterUrls.size) {
      Promise.all(Array.from(requesterUrls).map((url) => api.get(url))).then(
        (requesterResponses) =>
          setRequesters(
            requesterResponses.reduce(
              (acc, requesterResponse) => ({
                ...acc,
                [requesterResponse.data.url]: requesterResponse.data,
              }),
              requesters
            )
          )
      );
    }
    // Having reqeusters / accounts as dep would cause loop
    // eslint-disable-next-line
  }, [state?.transfers]);

  return (
    <>
      <TableTitle
        icon={transferIcon}
        title="Pending Account Deposits and Withdrawals"
      />

      <BaseTable
        columns={columns}
        rows={state.transfers || []}
        csvName="Transfers"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isLoading={state.pending}
        error={state.error}
        isSortable={false}
      />
    </>
  );
};
